/**
 * Zelium AI — Theme Configuration
 * ================================
 * Centralised, type-safe theme configuration object.
 * All style props are configurable — components consume these values
 * instead of hardcoding colours, sizes, or timings.
 *
 * Colour values are imported from ./colors.ts — the single source of truth.
 * To change a colour, edit colors.ts (and mirror in zelium-tokens.css).
 *
 * @see frontend/src/theme/colors.ts — raw colour palette
 * @see documents/ui-enhancement-spec.md §4, §9, §13
 */

import { SEMANTIC_COLORS, GRADE_COLORS, CONFETTI_COLORS } from './colors';

/* ─── Score Tiers ──────────────────────────────────────────── */

export type ScoreTier = 'critical' | 'warning' | 'good' | 'excellent';

export interface ScoreTierConfig {
  readonly range: [number, number];
  readonly label: string;
  readonly cssVar: string;
  readonly hex: string;
  readonly hasGlow: boolean;
  readonly polarisTone: 'critical' | 'warning' | 'success';
  readonly badgeTone: 'critical' | 'attention' | 'warning' | 'success';
}

export const SCORE_TIERS: Record<ScoreTier, ScoreTierConfig> = {
  critical: {
    range: [0, 39],
    label: 'AI-Invisible',
    cssVar: 'var(--z-score-critical)',
    hex: SEMANTIC_COLORS.critical,
    hasGlow: false,
    polarisTone: 'critical',
    badgeTone: 'critical',
  },
  warning: {
    range: [40, 69],
    label: 'AI-Confusing',
    cssVar: 'var(--z-score-warning)',
    hex: SEMANTIC_COLORS.warning,
    hasGlow: false,
    polarisTone: 'warning',
    badgeTone: 'attention',
  },
  good: {
    range: [70, 89],
    label: 'AI-Readable',
    cssVar: 'var(--z-score-good)',
    hex: SEMANTIC_COLORS.good,
    hasGlow: true,
    polarisTone: 'success',
    badgeTone: 'success',
  },
  excellent: {
    range: [90, 100],
    label: 'AI-Discoverable',
    cssVar: 'var(--z-score-excellent)',
    hex: SEMANTIC_COLORS.excellent,
    hasGlow: true,
    polarisTone: 'success',
    badgeTone: 'success',
  },
} as const;

export function getScoreTier(score: number): ScoreTier {
  if (score >= 90) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 40) return 'warning';
  return 'critical';
}

export function getScoreTierConfig(score: number): ScoreTierConfig {
  return SCORE_TIERS[getScoreTier(score)];
}

/* ─── Grade Colours ─────────────────────────────────────────── */

export type GradeLetter = 'A' | 'B' | 'C' | 'D' | 'F';

export const GRADE_COLOURS: Record<GradeLetter, { cssVar: string; hex: string }> = {
  A: { cssVar: 'var(--z-grade-a)', hex: GRADE_COLORS.A },
  B: { cssVar: 'var(--z-grade-b)', hex: GRADE_COLORS.B },
  C: { cssVar: 'var(--z-grade-c)', hex: GRADE_COLORS.C },
  D: { cssVar: 'var(--z-grade-d)', hex: GRADE_COLORS.D },
  F: { cssVar: 'var(--z-grade-f)', hex: GRADE_COLORS.F },
} as const;

/* ─── Score Gauge Dimensions ────────────────────────────────── */

export type GaugeSize = 'small' | 'medium' | 'large' | 'hero';

export interface GaugeDimensions {
  readonly diameter: number;
  readonly strokeWidth: number;
  readonly fontSize: number;
  readonly gradeSize: number;
}

export const GAUGE_SIZES: Record<GaugeSize, GaugeDimensions> = {
  small: { diameter: 60, strokeWidth: 4, fontSize: 16, gradeSize: 10 },
  medium: { diameter: 100, strokeWidth: 6, fontSize: 24, gradeSize: 14 },
  large: { diameter: 140, strokeWidth: 8, fontSize: 32, gradeSize: 13 },
  hero: { diameter: 160, strokeWidth: 10, fontSize: 40, gradeSize: 20 },
} as const;

/* ─── Motion Config ──────────────────────────────────────────── */

export interface MotionConfig {
  readonly duration: {
    readonly instant: number;
    readonly fast: number;
    readonly normal: number;
    readonly slow: number;
    readonly dramatic: number;
  };
  readonly ease: {
    readonly default: string;
    readonly enter: string;
    readonly exit: string;
    readonly bounce: string;
  };
  readonly stagger: {
    readonly default: number;
    readonly fast: number;
  };
  readonly maxStaggerItems: number;
}

export const MOTION: MotionConfig = {
  duration: {
    instant: 0.1,
    fast: 0.2,
    normal: 0.3,
    slow: 0.5,
    dramatic: 0.8,
  },
  ease: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    enter: 'cubic-bezier(0, 0, 0.2, 1)',
    exit: 'cubic-bezier(0.4, 0, 1, 1)',
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  stagger: {
    default: 0.08, // 80ms between items
    fast: 0.05,    // 50ms between items
  },
  maxStaggerItems: 6,
} as const;

/* ─── Confetti Config ────────────────────────────────────────── */

export interface ConfettiConfig {
  readonly colors: readonly string[];
  readonly gravity: number;
  readonly maxDuration: number;
  readonly disableForReducedMotion: boolean;
}

export type ConfettiIntensity = 'light' | 'medium' | 'full';

export const CONFETTI: Record<ConfettiIntensity, ConfettiConfig & { particles: number }> = {
  light: {
    particles: 30,
    colors: [...CONFETTI_COLORS],
    gravity: 1.2,
    maxDuration: 2500,
    disableForReducedMotion: true,
  },
  medium: {
    particles: 50,
    colors: [...CONFETTI_COLORS],
    gravity: 1.2,
    maxDuration: 2500,
    disableForReducedMotion: true,
  },
  full: {
    particles: 80,
    colors: [...CONFETTI_COLORS],
    gravity: 1.2,
    maxDuration: 2500,
    disableForReducedMotion: true,
  },
} as const;

/* ─── Chart Config ───────────────────────────────────────────── */

export interface ChartConfig {
  readonly width: number;
  readonly height: number;
  readonly padding: { top: number; right: number; bottom: number; left: number };
  readonly barGap: number;
}

export const CHART_DEFAULTS: ChartConfig = {
  width: 600,
  height: 200,
  padding: { top: 20, right: 20, bottom: 40, left: 50 },
  barGap: 12,
} as const;

export const HISTOGRAM_DEFAULTS: ChartConfig = {
  width: 400,
  height: 180,
  padding: { top: 20, right: 20, bottom: 40, left: 50 },
  barGap: 12,
} as const;

/* ─── Sub-Score Config ───────────────────────────────────────── */

export interface SubScoreFieldConfig {
  readonly key: string;
  readonly label: string;
  readonly weight: string;
  readonly description: string;
}

export const SUB_SCORE_FIELDS: readonly SubScoreFieldConfig[] = [
  {
    key: 'llmTextQuality',
    label: 'Text Quality',
    weight: '25%',
    description:
      'AI-assessed readability, keyword relevance, and specificity of your product title and description. Higher scores mean AI assistants can better understand and recommend your product.',
  },
  {
    key: 'dataCompleteness',
    label: 'Data Completeness',
    weight: '20%',
    description:
      'Checks whether essential product fields are filled in: title (ideal 50–80 chars), description (300+ chars recommended), vendor, product type, and images (3+ recommended). Category-specific attributes are evaluated separately and may cap your overall score.',
  },
  {
    key: 'schemaQuality',
    label: 'Schema Quality',
    weight: '20%',
    description:
      'Evaluates the structured data (JSON-LD) on your storefront product page — including Product schema type, offer information, canonical URL, and breadcrumb navigation. Structured data helps AI engines and search crawlers understand your products.',
  },
  {
    key: 'identifierTaxonomy',
    label: 'Identifier & Taxonomy',
    weight: '15%',
    description:
      'Measures the presence and quality of product identifiers: GTIN/barcode, unique SKU, MPN, product type classification, and tags. Strong identifiers help AI assistants match and surface your products accurately.',
  },
  {
    key: 'mediaQuality',
    label: 'Media Quality',
    weight: '10%',
    description:
      'Assesses your product images: at least 1 image required, 3+ images recommended, and descriptive alt text on images. Good media helps both visual AI search and accessibility.',
  },
  {
    key: 'internalConsistency',
    label: 'Internal Consistency',
    weight: '10%',
    description:
      'Checks alignment between your product data fields — whether title keywords appear in the description, product type matches the title, and SKUs are unique. Consistent data builds AI confidence in your products.',
  },
] as const;

/* ─── Severity Mapping ───────────────────────────────────────── */

export type SeverityLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export const SEVERITY_TONES: Record<SeverityLevel, 'critical' | 'warning' | 'attention' | 'info'> = {
  CRITICAL: 'critical',
  HIGH: 'warning',
  MEDIUM: 'attention',
  LOW: 'info',
} as const;

export const SUGGESTION_SEVERITY_TONES: Record<string, 'critical' | 'warning' | 'info' | 'success'> = {
  CRITICAL: 'critical',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'success',
} as const;

export const CATEGORY_LABELS: Record<string, string> = {
  DATA_COMPLETENESS: 'Data Completeness',
  SCHEMA_QUALITY: 'Schema Quality',
  IDENTIFIER: 'Identifier',
  TEXT_QUALITY: 'Text Quality',
  MEDIA: 'Media',
  CONSISTENCY: 'Consistency',
} as const;

/* ─── Compliance Check Labels ────────────────────────────────── */

export const COMPLIANCE_CHECK_LABELS: Record<string, string> = {
  JSON_LD_VALID: 'JSON-LD Structured Data',
  SCHEMA_TYPE_PRODUCT: 'Product Schema Type',
  OFFERS_COMPLETE: 'Complete Offer Information',
  BRAND_PRESENT: 'Brand Information',
  IMAGE_URL_VALID: 'Valid Image URLs',
  IMAGE_ALT_TEXT: 'Image Alt Text',
  NO_DUPLICATE_SCHEMA: 'No Duplicate Schemas',
  CANONICAL_URL_VALID: 'Canonical URL',
  OG_TAGS_PRESENT: 'Open Graph Tags',
  PRICE_TEXT_RENDERED: 'Visible Price Text',
} as const;

/* ─── Scan Status Labels ─────────────────────────────────────── */

export const SCAN_STATUS_LABELS: Record<string, string> = {
  QUEUED: 'Queued…',
  RUNNING: 'Scanning products…',
  COMPLETED: 'Scan complete',
  FAILED: 'Scan failed',
  CANCELLED: 'Scan cancelled',
} as const;

/* ─── Onboarding Value Props ─────────────────────────────────── */

export const ONBOARDING_VALUE_PROPS = [
  'See how AI engines like ChatGPT, Perplexity, and Google Gemini view your products',
  'Get an AI Discoverability Score for your catalog (0–100)',
  'Receive actionable suggestions to improve your product visibility',
] as const;
